/**
 * Buying an app.
 *
 * THE ORDER IS NOT THE OBVIOUS ONE. A free app's consent is: choose a realm → the grant
 * exists. A paid app's is: choose a realm → PAY → the grant exists. So `startCheckout`
 * creates no authorization; it records what was chosen, sends the buyer to Stripe, and
 * the return page finishes the grant they already decided on.
 */
export default {
  /** Begin paying. Returns {checkout_url} to send the buyer to, or {already_purchased}. */
  async startCheckout(body) {
    return this.request('/billing/checkout', { method: 'POST', body });
  },

  /**
   * Verify a payment on return from Stripe, and get back what to authorize with.
   *
   * PULLS rather than waiting for the webhook. Stripe redirects the buyer back
   * immediately and the webhook is asynchronous, so a return page that assumed the
   * webhook had landed would tell a paying customer they had not paid — at the worst
   * possible moment to be wrong.
   */
  async confirmCheckout(sessionId) {
    return this.request('/billing/checkout/confirm', {
      method: 'POST',
      body: { session_id: sessionId },
    });
  },

  /** Everything this person pays for, each from its own frozen agreed price. */
  async listPurchases() {
    return this.request('/billing/purchases');
  },

  /** What has actually been charged. Held by us because we are the merchant of
   *  record — a receipts page that empties when Stripe is unreachable is not a record. */
  async listInvoices() {
    return this.request('/billing/invoices');
  },

  /**
   * Take the refund the platform guarantees. Inside the published window this needs no
   * approval and asks for no reason — a floor somebody has to request is a discretion,
   * which is the thing the commitment refuses to be.
   */
  async refundPurchase(purchaseId) {
    return this.request(`/billing/purchases/${purchaseId}/refund`, { method: 'POST' });
  },

  /** Where to change the card a purchase is charged to: { url } of a Stripe portal
   *  page that comes back to `returnUrl` (one of ours, else the billing page). */
  async updatePaymentMethod(purchaseId, returnUrl) {
    return this.request(`/billing/purchases/${purchaseId}/payment-method`, {
      method: 'POST', body: { return_url: returnUrl },
    });
  },

  /** Stop a subscription renewing; access lasts until the period paid for ends
   *  ({ purchase, access_until }). Disconnecting the app is what ends it now. */
  async cancelSubscription(purchaseId) {
    return this.request(`/billing/purchases/${purchaseId}/cancel`, { method: 'POST' });
  },

  /** Take back a cancel at period end: the subscription renews again ({ purchase }). */
  async resumeSubscription(purchaseId) {
    return this.request(`/billing/purchases/${purchaseId}/resume`, { method: 'POST' });
  },
};
